import type { QueryClient } from "@tanstack/react-query";

import { NameDisplayOrder } from "@/types/enums/name-display-order";
import type { ProviderSummary } from "@/types/provider";

type StaffNameFields = {
  firstName?: string | null;
  lastName?: string | null;
  credentials?: string | null;
} & Partial<
  Pick<ProviderSummary, "nameDisplayOrder" | "showCredentialsInName">
>;

/**
 * A staff member to name: their id, or an object with their id. Names on the
 * object are only a fallback for ids not in the staff list (a patient who
 * sent a message, say), which show as plain First Last.
 */
export type StaffRef =
  | string
  | ({ id?: string | number | null } & StaffNameFields);

export interface ResolvedStaffName {
  firstName?: string | null;
  lastName?: string | null;
  order: NameDisplayOrder;
  credentials: string | null;
}

// Where staff lists sit in the React Query cache. Spelled out rather than
// imported: the query modules pull in the API client, and this file sits under
// format-data, which nearly everything imports.
// The console's provider lists (providerQueryKeys.lists()); the unscoped one
// holds every staff member, archived included.
const PROVIDER_LISTS_KEY = ["providers", "list"];
// Online booking's front page (selfScheduleQueryKeys.frontPage()).
const BOOKING_FRONT_PAGE_KEY = ["calendar", "self-schedule", "front-page"];

type StaffRecord = { id: string } & StaffNameFields;

// The clinic's staff, by id: the current name and how each person chose to
// show it. Filled from the query cache by loadStaffNamesFrom.
let staffById = new Map<string, StaffNameFields>();

const startsWith = (key: readonly unknown[], prefix: readonly unknown[]) =>
  prefix.every((part, index) => key[index] === part);

/** True for the query keys whose data loadStaffNamesFrom reads. */
export function isStaffListKey(key: readonly unknown[]): boolean {
  return (
    startsWith(key, PROVIDER_LISTS_KEY) ||
    startsWith(key, BOOKING_FRONT_PAGE_KEY)
  );
}

/**
 * Rebuild the known staff from the staff lists in this query cache. The shared
 * query client calls it whenever one of those lists changes
 * (config/react-query.tsx).
 */
export function loadStaffNamesFrom(client: QueryClient): void {
  const next = new Map<string, StaffNameFields>();
  const frontPage = client.getQueryData<{ providers?: StaffRecord[] } | null>(
    BOOKING_FRONT_PAGE_KEY,
  );
  for (const person of frontPage?.providers ?? []) next.set(person.id, person);
  for (const [, staff] of client.getQueriesData<StaffRecord[] | null>({
    queryKey: PROVIDER_LISTS_KEY,
  })) {
    for (const person of staff ?? []) next.set(person.id, person);
  }
  staffById = next;
}

export function resolveStaffName(
  staff: StaffRef | null | undefined,
): ResolvedStaffName | undefined {
  const ref = typeof staff === "string" ? { id: staff } : staff;
  if (!ref) return undefined;
  const known = ref.id != null ? staffById.get(String(ref.id)) : undefined;
  const person = known ?? ref;
  // Display choices apply only to staff: a known record, or a full provider.
  const isStaff = known !== undefined || ref.nameDisplayOrder !== undefined;
  return {
    firstName: person.firstName,
    lastName: person.lastName,
    order: (isStaff && person.nameDisplayOrder) || NameDisplayOrder.FIRST_LAST,
    // On unless the person turned it off.
    credentials:
      isStaff && person.showCredentialsInName !== false
        ? person.credentials?.trim() || null
        : null,
  };
}

/** True when `formatStaffName(staff)` already ends the name with their credentials. */
export function nameShowsCredentials(
  staff: StaffRef | null | undefined,
): boolean {
  const resolved = resolveStaffName(staff);
  return !!resolved?.firstName && !!resolved.credentials;
}

/**
 * A staff member's name and credentials the way the backend writes them into a
 * patient text: plain "First Last" for `{provider_name}` (no display order, no
 * credentials) and the stored credentials for `{provider_credentials}`, whether
 * or not the person shows them in their console name
 * (`decoda/services/scheduling.py` `_get_event_provider_suggestions`). Use it to
 * preview an SMS; `formatStaffName` would put the credentials in the name, and
 * a template reading `{provider_name}, {provider_credentials}` would then show
 * them twice.
 */
export function staffSmsNameParts(staff: StaffRef | null | undefined): {
  name: string | null;
  firstName: string | null;
  credentials: string | null;
} {
  const ref = typeof staff === "string" ? { id: staff } : staff;
  if (!ref) return { name: null, firstName: null, credentials: null };
  const person =
    (ref.id != null ? staffById.get(String(ref.id)) : undefined) ?? ref;
  const firstName = person.firstName?.trim() || null;
  const name =
    [firstName, person.lastName?.trim()].filter(Boolean).join(" ") || null;
  return { name, firstName, credentials: person.credentials?.trim() || null };
}
